import React, { ReactNode, useEffect, useMemo } from "react"
import { matchPath, useLocation } from "react-router-dom"

import { useEventSession } from "@/hooks/use-event-session"
import { Pages } from "@/pages/constants"
import { routes } from "@/pages/routes"

import { EVENTSESSIONS, PageSessionType } from "./session"

type EventSessionsContextType = {
  startSession: (name: string) => void
  endSession: (name: string) => void
  getSession: (name: string) => string
  sessions: any
}

export const EventSessionsContext = React.createContext<EventSessionsContextType>({
  startSession: () => null,
  endSession: () => null,
  getSession: (name: string) => name,
  sessions: null,
})

type ProviderProps = {
  children: ReactNode
}

export const EventSessionsProvider = (props: ProviderProps) => {
  const { children } = props
  const location = useLocation()
  const { eventSessions, startSession, endSession, getSession } = useEventSession()

  const values = useMemo(
    () => ({
      startSession,
      endSession,
      getSession,
      sessions: Object.values(eventSessions),
    }),
    [startSession, endSession, getSession, eventSessions],
  )

  useEffect(() => {
    let pageSessions: PageSessionType
    routes.forEach((route) => {
      const isMatched = matchPath(location.pathname, {
        path: route.path,
        exact: route?.options?.exact,
        strict: route?.options?.strict,
      })
      if (isMatched) {
        pageSessions = EVENTSESSIONS[isMatched.path as Pages]
        pageSessions.onStart.forEach((session: string) => {
          const temp = getSession(session)
          if (!temp) {
            startSession(session)
          }
        })
      }
    })
    return () => {
      pageSessions.onLeave.forEach((session: string) => {
        const temp = getSession(session)
        if (temp) {
          endSession(session)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <EventSessionsContext.Provider value={values}>
      {children}
    </EventSessionsContext.Provider>
  )
}
